import * as React from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { DeleteButton } from 'components/Common/PepV2DeleteButton';

export type DynamicFieldItem = {
  id: string;
  label: string;
  value: string;
};

export type DynamicFieldError = {
  label?: string;
  value?: string;
};

export interface DynamicFieldSectionProps {
  fields?: DynamicFieldItem[];
  setFields?: (fields: DynamicFieldItem[]) => void;
  onSave?: (fields: DynamicFieldItem[]) => void;

  title?: string;
  addButtonText?: string;
  saveButtonText?: string;
  labelPlaceholder?: string;
  valuePlaceholder?: string;

  minFields?: number;
  maxFields?: number;

  required?: boolean;
  validateOnChange?: boolean;
  showSaveButton?: boolean;

  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;

  fullWidth?: boolean;

  saveButtonDisabled?: boolean;
  addButtonDisabled?: boolean;

  hideRemoveForFirstItem?: boolean;

  containerSx?: SxProps<Theme>;
  rowSx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
  addButtonSx?: SxProps<Theme>;
  saveButtonSx?: SxProps<Theme>;
  removeButtonSx?: SxProps<Theme>;

  colors?: {
    inputBackground?: string;
    inputBorder?: string;
    inputText?: string;
    inputPlaceholder?: string;
    labelText?: string;
    addText?: string;
    saveBackground?: string;
    saveText?: string;
    removeBorder?: string;
    removeIcon?: string;
    error?: string;
  };
}

const createField = (): DynamicFieldItem => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  label: '',
  value: '',
});

export default function DynamicLabelValueFields({
  fields,
  setFields,
  onSave,
  title,
  addButtonText = '+ ADD FIELD',
  saveButtonText = 'SAVE',
  labelPlaceholder = 'Label',
  valuePlaceholder = 'Value',
  minFields = 1,
  maxFields,
  required = true,
  validateOnChange = true,
  showSaveButton = true,
  disabled = false,
  readOnly = false,
  loading = false,
  fullWidth = true,
  saveButtonDisabled = false,
  addButtonDisabled = false,
  hideRemoveForFirstItem = false,
  containerSx,
  rowSx,
  inputSx,
  addButtonSx,
  saveButtonSx,
  removeButtonSx,
  colors: colorsProp,
}: DynamicFieldSectionProps) {
  const theme = useTheme();
  const color = theme.palette;

  const defaultColors = {
    inputBackground: color.inputNew.inputBackground,
    inputBorder: color.inputNew.inputBorder,
    inputText: color.inputNew.inputText,
    inputPlaceholder: color.inputNew.inputPlaceholder,
    labelText: color.inputNew.labelText,
    addText: color.inputNew.addText,
    saveBackground: color.inputNew.saveBackground,
    saveText: color.inputNew.saveText,
    removeBorder: color.inputNew.removeBorder,
    removeIcon: color.inputNew.removeIcon,
    error: color.inputNew.error,
  };

  const colors = React.useMemo(() => ({ ...defaultColors, ...colorsProp }), [colorsProp]);

  const isControlled = fields !== undefined;

  const [internalFields, setInternalFields] = React.useState<DynamicFieldItem[]>(
    fields && fields.length > 0 ? fields : Array.from({ length: minFields }, () => createField()),
  );

  const [errors, setErrors] = React.useState<Record<string, DynamicFieldError>>({});

  const currentFields = isControlled ? (fields as DynamicFieldItem[]) : internalFields;

  React.useEffect(() => {
    if (isControlled && fields) {
      setInternalFields(
        fields.length > 0 ? fields : Array.from({ length: minFields }, () => createField()),
      );
    }
  }, [fields, isControlled, minFields]);

  const updateFields = (updated: DynamicFieldItem[]) => {
    if (!isControlled) {
      setInternalFields(updated);
    }
    setFields?.(updated);
  };

  const validateField = (field: DynamicFieldItem): DynamicFieldError => {
    const fieldErrors: DynamicFieldError = {};

    if (required) {
      if (!field.label.trim()) fieldErrors.label = 'Label is required';
      if (!field.value.trim()) fieldErrors.value = 'Value is required';
    }

    return fieldErrors;
  };

  const validateAll = (items: DynamicFieldItem[]) => {
    const nextErrors: Record<string, DynamicFieldError> = {};

    items.forEach((field) => {
      const fieldError = validateField(field);
      if (fieldError.label || fieldError.value) {
        nextErrors[field.id] = fieldError;
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleFieldChange = (id: string, key: 'label' | 'value', value: string) => {
    const updated = currentFields.map((field) =>
      field.id === id ? { ...field, [key]: value } : field,
    );

    updateFields(updated);

    if (validateOnChange) {
      const changedField = updated.find((f) => f.id === id);
      if (changedField) {
        setErrors((prev) => ({
          ...prev,
          [id]: validateField(changedField),
        }));
      }
    }
  };

  const handleAddField = () => {
    if (disabled || readOnly) return;
    if (maxFields && currentFields.length >= maxFields) return;

    const updated = [...currentFields, createField()];
    updateFields(updated);
  };

  const handleRemoveField = (id: string) => {
    if (disabled || readOnly) return;
    if (currentFields.length <= minFields) return;

    const updated = currentFields.filter((field) => field.id !== id);
    updateFields(updated);

    setErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleSave = () => {
    const isValid = validateAll(currentFields);
    if (!isValid) return;
    onSave?.(currentFields);
  };

  const canRemove = (index: number) => {
    if (currentFields.length <= minFields) return false;
    if (hideRemoveForFirstItem && index === 0) return false;
    return true;
  };

  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : 'auto',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        p: 2,
        borderRadius: 1,
        ...containerSx,
      }}
    >
      {title && (
        <Typography
          variant="widgetTitle"
          sx={{
            mb: 1.5,
            color: colors.labelText,
          }}
        >
          {title}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        {currentFields.map((field, index) => {
          const fieldError = errors[field.id] || {};

          return (
            <Grid
              container
              gap={2}
              key={field.id}
              alignItems="flex-start"
              sx={{
                width: '100%',
                margin: 0,
                ...rowSx,
              }}
            >
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={labelPlaceholder}
                  value={field.label}
                  disabled={disabled}
                  onChange={(e) => handleFieldChange(field.id, 'label', e.target.value)}
                  error={Boolean(fieldError.label)}
                  helperText={fieldError.label || ' '}
                  InputProps={{
                    readOnly,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: colors.inputBackground,
                      color: colors.inputText,
                      borderRadius: '4px',
                      '& fieldset': {
                        borderColor: colors.inputBorder,
                      },
                      '&:hover fieldset': {
                        borderColor: colors.inputBorder,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.inputBorder,
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: colors.inputPlaceholder,
                      opacity: 1,
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: 0,
                      color: colors.error,
                    },
                    ...inputSx,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={valuePlaceholder}
                  value={field.value}
                  disabled={disabled}
                  onChange={(e) => handleFieldChange(field.id, 'value', e.target.value)}
                  error={Boolean(fieldError.value)}
                  helperText={fieldError.value || ' '}
                  InputProps={{
                    readOnly,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: colors.inputBackground,
                      color: colors.inputText,
                      borderRadius: '4px',
                      '& fieldset': {
                        borderColor: colors.inputBorder,
                      },
                      '&:hover fieldset': {
                        borderColor: colors.inputBorder,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.inputBorder,
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: colors.inputPlaceholder,
                      opacity: 1,
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: 0,
                      color: colors.error,
                    },
                    ...inputSx,
                  }}
                />
              </Grid>

              {canRemove(index) ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', md: 'center' },
                    width: { xs: '100%', md: 'auto' },
                    flexShrink: 0,
                  }}
                >
                  <DeleteButton 
                    variant="temporary" 
                    onClick={() => handleRemoveField(field.id)} 
                    ariaLabel={`remove-field-${index}`}
                  />
                </Box>
              ) : null}
            </Grid>
          );
        })}

        <Grid container gap={2} sx={{ width: '100%' }}>
          <Grid item xs={12} md={4}>
            <Button
              variant="pepwiseButtonText"
              onClick={handleAddField}
              disabled={
                disabled ||
                readOnly ||
                addButtonDisabled ||
                (Boolean(maxFields) && currentFields.length >= (maxFields as number))
              }
              sx={{
                p: 0,
                minWidth: 'auto',
                justifyContent: 'flex-start',
                alignSelf: { xs: 'flex-start', sm: 'auto' },
                color: colors.addText,
                '&:hover': {
                  backgroundColor: 'transparent',
                  opacity: 0.85,
                },
                '&.Mui-disabled': {
                  backgroundColor: colors.inputBorder,
                },
                ...addButtonSx,
              }}
            >
              {addButtonText}
            </Button>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}
          >
            {showSaveButton ? (
              <Button
                variant="pepwiseSecondary"
                onClick={handleSave}
                disabled={disabled || loading || saveButtonDisabled}
                sx={{
                  minWidth: { xs: '100%', sm: 164 },
                  width: { xs: '100%', sm: 'auto' },
                  backgroundColor: colors.saveBackground,
                  color: colors.saveText,
                  '&:hover': {
                    backgroundColor: colors.saveBackground,
                    boxShadow: 'none',
                    opacity: 0.92,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: colors.inputBorder,
                  },
                  ...saveButtonSx,
                }}
              >
                {loading ? 'Saving...' : saveButtonText}
              </Button>
            ) : null}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
